/**
 * The two object-menu styles (TMMAPP-0099, visual contract §11); see `OverflowMenu.tsx`.
 *
 * - `page` — the kebab beside a page `h1` (P:2472 / 2506): min-width 200, padding 4 0; items 13px,
 *   padding 10 16, with a `line` rule above every item.
 * - `card` — the kebab on a card or a row (P:2600–2607): 184 wide, padding 6 0; items 14px,
 *   padding 9 16, no rules.
 *
 * Both carry no icons and paint a destructive item `errorText` (`destructiveItemSx`).
 */
import type { MenuProps } from '@mui/material';
import { raisedLine } from '@/theme/raisedLine';
import type { AppTokens } from '@/theme/tokens';

export type ObjectMenuVariant = 'page' | 'card';

type MenuSlotProps = NonNullable<MenuProps['slotProps']>;

/** The `slotProps` that give a `Menu` one of the two object-menu styles. */
export function objectMenuSlotProps(tokens: AppTokens, variant: ObjectMenuVariant): MenuSlotProps {
  const page = variant === 'page';
  return {
    paper: {
      'data-menu-variant': variant,
      sx: page ? { minWidth: 200 } : { width: 184, maxWidth: '100%' },
    } as NonNullable<MenuSlotProps['paper']>,
    list: {
      sx: {
        paddingBlock: page ? '4px' : '6px',
        // Object menus carry no icons and no extra dividers: the variant draws its own rules.
        '& .MuiListItemIcon-root, & > .MuiDivider-root': { display: 'none' },
        '& .MuiMenuItem-root': {
          minHeight: 0,
          fontSize: page ? 13 : 14,
          lineHeight: page ? '20px' : '21px',
          padding: page ? '10px 16px' : '9px 16px',
          whiteSpace: 'normal',
          ...(page ? { borderTop: `1px solid ${raisedLine(tokens)}` } : {}),
        },
      },
    },
  };
}

/** The page kebab beside an `h1` (visual contract §4): 30×30, `text3` dots. */
export function pageKebabSx(tokens: AppTokens) {
  return { width: 30, height: 30, p: 0, color: tokens.text3 };
}

/** A destructive object-menu item: `errorText` on `surfaceRaised` (and under its hover). */
export function destructiveItemSx(tokens: AppTokens) {
  return { color: tokens.errorText };
}
